import { SUPABASE_URL } from '../utils/supabase/url';
/** Plant register service — wraps the workspace-scoped plant-asset endpoints.
 *  Cached like ToolboxTalksService, but keyed on workspace rather than
 *  project: a fleet is a workspace asset that moves between sites. */

import { projectId as supabaseProjectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/supabase/client';
import type {
  PlantAsset,
  PlantAssetStatus,
  CreatePlantAssetInput,
  UpdatePlantAssetInput,
} from '../types/plant';

const API_BASE = `${SUPABASE_URL}/functions/v1/make-server-a476273b`;
const PLANT_ASSETS_LIST_CACHE_MS = 60_000;

interface PlantAssetsListOptions {
  status?: PlantAssetStatus;
  q?: string;
  force?: boolean;
}

interface PlantAssetsListCacheEntry {
  records: PlantAsset[];
  expiresAt: number;
}

// Only the unfiltered list is cached. A filtered or searched call is cheap,
// rare, and caching every query string would just grow a map nobody evicts.
const listCache = new Map<string, PlantAssetsListCacheEntry>();
const listRequests = new Map<string, Promise<PlantAsset[]>>();

async function authedFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = await getAuthHeaders({ 'Content-Type': 'application/json' });
  const res = await fetch(url, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Request failed (${res.status})`);
  }
  return res;
}

function workspaceBase(workspaceId: string): string {
  return `${API_BASE}/workspaces/${encodeURIComponent(workspaceId)}/plant-assets`;
}

export const PlantAssetsService = {
  peekList(workspaceId: string): PlantAsset[] | undefined {
    const cached = listCache.get(workspaceId);
    if (!cached) return undefined;
    if (cached.expiresAt <= Date.now()) {
      listCache.delete(workspaceId);
      return undefined;
    }
    return cached.records;
  },

  async list(workspaceId: string, options: PlantAssetsListOptions = {}): Promise<PlantAsset[]> {
    const { status, q, force = false } = options;
    const filtered = Boolean(status || q);

    if (!filtered) {
      const activeRequest = listRequests.get(workspaceId);
      if (activeRequest) return activeRequest;
      if (!force) {
        const cached = PlantAssetsService.peekList(workspaceId);
        if (cached) return cached;
      }
    }

    const url = new URL(workspaceBase(workspaceId));
    if (status) url.searchParams.set('status', status);
    if (q) url.searchParams.set('q', q);

    const request = (async () => {
      const res = await authedFetch(url.toString());
      const data = await res.json() as { assets: PlantAsset[] };
      const records = data.assets ?? [];
      if (records.some(record => record.workspaceId !== workspaceId)) {
        // Fail closed on a mixed-workspace payload, as the H&S services do.
        throw new Error('Plant assets response was outside the requested workspace scope');
      }
      if (!filtered) {
        listCache.set(workspaceId, { records, expiresAt: Date.now() + PLANT_ASSETS_LIST_CACHE_MS });
      }
      return records;
    })();

    if (!filtered) {
      listRequests.set(workspaceId, request);
      try {
        return await request;
      } finally {
        if (listRequests.get(workspaceId) === request) listRequests.delete(workspaceId);
      }
    }
    return request;
  },

  prefetchList(workspaceId: string): Promise<PlantAsset[]> {
    return PlantAssetsService.list(workspaceId);
  },

  async get(id: string): Promise<PlantAsset> {
    const res = await authedFetch(`${API_BASE}/plant-assets/${encodeURIComponent(id)}`);
    const data = await res.json() as { asset: PlantAsset };
    return data.asset;
  },

  async create(workspaceId: string, input: CreatePlantAssetInput): Promise<PlantAsset> {
    const res = await authedFetch(workspaceBase(workspaceId), {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await res.json() as { asset: PlantAsset };
    listCache.delete(workspaceId);
    return data.asset;
  },

  async update(id: string, patch: UpdatePlantAssetInput): Promise<PlantAsset> {
    const res = await authedFetch(`${API_BASE}/plant-assets/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
    const data = await res.json() as { asset: PlantAsset };
    listCache.delete(data.asset.workspaceId);
    return data.asset;
  },

  /** Refused with a 409 by the server when a check sheet still references the
   *  machine, because a signed sheet must keep naming the plant it covered. */
  async remove(workspaceId: string, id: string): Promise<void> {
    await authedFetch(`${API_BASE}/plant-assets/${encodeURIComponent(id)}`, { method: 'DELETE' });
    listCache.delete(workspaceId);
  },
};
